import React from "react"
import { Box, Button, Divider, Grid, Paper, Stack, Typography } from "@mui/material"
import CustomAutocomplete from "src/components/CustomAutocomplete"
import { NamedCommunity } from "src/services/api/companyMembers"
import { GatedCommunityProfileStatus } from "src/services/api/gatedCommunities"
import ViewButton from "src/components/table/ViewButton"
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined"

const MEMBER_PROFILE_OPTIONS: GatedCommunityProfileStatus[] = ["GATED_COMMUNITY_MEMBER", "GATED_COMMUNITY_ADMIN"]

const getMemberProfileLabel = (p: GatedCommunityProfileStatus) =>
  p === "GATED_COMMUNITY_ADMIN" ? "Manager" : "Member"

export type CommunityItem = NamedCommunity & { toRemove: boolean }

export interface AddableCommunity {
  id: string
  name: string
  toAdd: boolean
  profile: GatedCommunityProfileStatus
}

interface EditMemberCommunitiesTabProps {
  communityItems: CommunityItem[]
  addableCommunities: AddableCommunity[]
  managerCommunityIds: Set<string>
  onRemove: (communityId: string) => void
  onUndo: (communityId: string) => void
  onProfileChange: (communityId: string, profile: GatedCommunityProfileStatus) => void
  onAdd: (id: string) => void
  onUndoAdd: (id: string) => void
  onToAddProfileChange: (id: string, profile: GatedCommunityProfileStatus) => void
}

interface ProfilePickerRowProps {
  value: GatedCommunityProfileStatus
  onChange: (profile: GatedCommunityProfileStatus) => void
  onRemove: () => void
}

const ProfilePickerRow = ({ value, onChange, onRemove }: ProfilePickerRowProps) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Box sx={{ flex: 1 }}>
      <CustomAutocomplete<GatedCommunityProfileStatus>
        label="Member Profile"
        options={MEMBER_PROFILE_OPTIONS}
        value={value}
        getOptionLabel={getMemberProfileLabel}
        isOptionEqualToValue={(a, b) => a === b}
        onChange={(_, val) => {
          if (val === "GATED_COMMUNITY_ADMIN" || val === "GATED_COMMUNITY_MEMBER") {
            onChange(val)
          }
        }}
        size="small"
      />
    </Box>
    <ViewButton size="small" onClick={onRemove}>
      <PersonOffOutlinedIcon fontSize="small" />
    </ViewButton>
  </Stack>
)

interface CommunityCardProps {
  name: string
  backgroundColor: string
  opacity?: number
  children: React.ReactNode
}

const CommunityCard = ({ name, backgroundColor, opacity = 1, children }: CommunityCardProps) => (
  <Paper
    variant="outlined"
    sx={{ borderRadius: "15px", backgroundColor, opacity, transition: "opacity 0.2s, background-color 0.2s" }}
  >
    <Stack>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, p: 2 }}>{name}</Typography>
      <Divider />
      <Box sx={{ p: 2 }}>
        {children}
      </Box>
    </Stack>
  </Paper>
)

export const EditMemberCommunitiesTab = ({
  communityItems,
  addableCommunities,
  managerCommunityIds,
  onRemove,
  onUndo,
  onProfileChange,
  onAdd,
  onUndoAdd,
  onToAddProfileChange
}: EditMemberCommunitiesTabProps) => (
  <Grid container spacing={2}>
    {communityItems.map((item) => {
      const isReadOnly = item.communityName === "My Knowledge Base" || !managerCommunityIds.has(item.communityId)
      return (
        <Grid item xs={12} sm={6} key={item.communityId}>
          <CommunityCard
            name={item.communityName}
            backgroundColor={item.toRemove ? "grey.200" : "common.white"}
            opacity={item.toRemove ? 0.6 : 1}
          >
            {isReadOnly ? (
              <Typography variant="body2" color="text.secondary">
                {getMemberProfileLabel(item.communityProfile)}
              </Typography>
            ) : !item.toRemove ? (
              <ProfilePickerRow
                value={item.communityProfile}
                onChange={(val) => onProfileChange(item.communityId, val)}
                onRemove={() => onRemove(item.communityId)}
              />
            ) : (
              <Button variant="outlined" color="secondary" size="small" sx={{ alignSelf: "flex-start" }} onClick={() => onUndo(item.communityId)}>
                Add member
              </Button>
            )}
          </CommunityCard>
        </Grid>
      )
    })}
    {addableCommunities.map((c) => (
      <Grid item xs={12} sm={6} key={c.id}>
        <CommunityCard name={c.name} backgroundColor={c.toAdd ? "primary.light" : "common.white"}>
          {c.toAdd ? (
            <ProfilePickerRow
              value={c.profile}
              onChange={(val) => onToAddProfileChange(c.id, val)}
              onRemove={() => onUndoAdd(c.id)}
            />
          ) : (
            <Button variant="outlined" color="secondary" size="small" sx={{ alignSelf: "flex-start" }} onClick={() => onAdd(c.id)}>
              Add member
            </Button>
          )}
        </CommunityCard>
      </Grid>
    ))}
    {communityItems.length === 0 && addableCommunities.length === 0 && (
      <Grid item xs={12}>
        <Typography variant="body1" color="text.secondary">—</Typography>
      </Grid>
    )}
  </Grid>
)
